import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { teamService } from '@/services/teamService';
import { useAuth } from '@/providers/AuthProvider';
import { Skeleton } from '@/components/ui/skeleton';
import type { PageProps } from '@/types/page';
import type { UserTeamInfo, TeamInvite, TeamMemberRole } from '@arrowise/shared';
import {
    Users,
    Settings,
    Mail,
    Check,
    X,
    Crown,
    User,
    Shield,
} from 'lucide-react';

export const TeamsPage: React.FC<PageProps> = ({ nav = true, footer = true }) => {
    const { user, refreshTeams } = useAuth();
    const navigate = useNavigate();
    const [teams, setTeams] = useState<UserTeamInfo[]>([]);
    const [pendingInvites, setPendingInvites] = useState<TeamInvite[]>([]);
    const [loading, setLoading] = useState(true);
    // Team creation disabled for now

    useEffect(() => {
        loadTeamsData();
    }, []);

    const loadTeamsData = async () => {
        try {
            setLoading(true);
            const data = await teamService.getUserTeams();
            setTeams(data.teams);
            setPendingInvites(data.pendingInvites);

            // Team names are now populated from backend via invite.team

            // If user has teams, automatically navigate to the first team's details page
            if (data.teams.length > 0) {
                const firstTeam = data.teams[0];
                navigate(`/app/teams/${firstTeam.team.id}`);
                return;
            }
        } catch (error) {
            console.error('Failed to load teams:', error);
            toast.error('Failed to load teams');
        } finally {
            setLoading(false);
        }
    };

    // No need to resolve invite team names on the client anymore

    // Team creation handler removed — creation disabled on frontend

    const handleAcceptInvite = async (invite: TeamInvite) => {
        try {
            await teamService.acceptTeamInvite(invite.token);
            // Immediately refresh global auth team state so guards reflect new membership
            await refreshTeams();
            toast.success(`Successfully joined ${invite.team?.name || 'the team'}`);

            // Navigate to the team details page
            if (invite.teamId) {
                navigate(`/app/teams/${invite.teamId}`);
            } else {
                // Fallback: reload teams data and navigate to first team
                await loadTeamsData();
            }
        } catch (error: any) {
            console.error('Failed to accept invite:', error);
            toast.error(error.message || 'Failed to accept invite');
        }
    };

    const getRoleIcon = (role: TeamMemberRole) => {
        switch (role) {
            case 'owner':
                return <Crown className="size-4 text-yellow-600" />;
            case 'admin':
                return <Shield className="size-4 text-blue-600" />;
            default:
                return <User className="size-4 text-gray-600" />;
        }
    };

    const getRoleBadgeVariant = (role: TeamMemberRole) => {
        switch (role) {
            case 'owner':
                return 'default';
            case 'admin':
                return 'secondary';
            default:
                return 'outline';
        }
    };

    if (loading) {
        return (
            <PageLayout nav={nav} footer={footer}>
                <div className="px-4 py-10 mx-auto max-w-6xl w-full">
                    <div className="space-y-6">
                        <Skeleton className="h-8 w-32" />
                        <div className="grid gap-6 md:grid-cols-2">
                            <Skeleton className="h-48 rounded-xl" />
                            <Skeleton className="h-48 rounded-xl" />
                        </div>
                    </div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout nav={nav} footer={footer}>
            <div className="px-4 py-10 mx-auto max-w-6xl w-full">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-3xl font-bold tracking-tight">Team</h1>
                    {/* Team creation is disabled */}
                </div>

                {/* No teams state */}
                {teams.length === 0 && pendingInvites.length === 0 && (
                    <div className="text-center py-12">
                        <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-semibold mb-2">No team yet</h3>
                        <p className="text-muted-foreground mb-6">
                            You need to be part of a team to access team features.
                        </p>
                        {user?.billingInfo?.subscriptionTier && ['team'].includes(user.billingInfo.subscriptionTier) ? (
                            <Button disabled>
                                Create Your First Team (disabled)
                            </Button>
                        ) : (
                            <div className="space-y-3">
                                <p className="text-sm text-muted-foreground">
                                    To become a team owner, you need a team subscription.
                                </p>
                                <Button asChild>
                                    <Link to="/app/billing">
                                        Upgrade to Team Plan
                                    </Link>
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {/* Pending invites */}
                {pendingInvites.length > 0 && (
                    <div className="mb-8">
                        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                            <Mail className="size-5" />
                            Pending Invitations ({pendingInvites.length})
                        </h2>
                        <div className="grid gap-4">
                            {pendingInvites.map((invite) => (
                                <Card
                                    key={invite.id}
                                    className="border-blue-500/30 dark:border-blue-400/40"
                                >
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <CardTitle className="text-lg flex items-center gap-2">
                                                    Invitation to {invite.team?.name || 'Unknown Team'}
                                                </CardTitle>
                                                <CardDescription>
                                                    You've been invited as {invite.role}
                                                </CardDescription>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button size="sm" onClick={() => handleAcceptInvite(invite)}>
                                                    <Check />
                                                    Accept
                                                </Button>
                                                <Button size="sm" variant="outline">
                                                    <X />
                                                    Decline
                                                </Button>
                                            </div>
                                        </div>
                                    </CardHeader>
                                </Card>
                            ))}
                        </div>
                    </div>
                )}

                {/* Teams grid */}
                {teams.length > 0 && (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {teams.map((teamInfo) => (
                            <Card key={teamInfo.team.id} className="hover:shadow-md transition-shadow">
                                <CardHeader>
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <CardTitle className="flex items-center gap-2">
                                                {teamInfo.team.name}
                                                {getRoleIcon(teamInfo.role)}
                                            </CardTitle>
                                            <CardDescription className="mt-1">
                                                {teamInfo.team.description || 'No description'}
                                            </CardDescription>
                                        </div>
                                        <Badge variant={getRoleBadgeVariant(teamInfo.role)}>
                                            {teamInfo.role}
                                        </Badge>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Users className="size-4" />
                                            <span>Team slug: {teamInfo.team.slug}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <span>Joined: {new Date(teamInfo.membership.joinedAt).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                </CardContent>
                                <CardFooter>
                                    <div className="flex gap-2">
                                        {(teamInfo.role === 'owner' || teamInfo.role === 'admin') && (
                                            <Button size="sm" variant="outline" asChild>
                                                <Link to={`/app/teams/${teamInfo.team.id}/manage`}>
                                                    <Settings />
                                                    Manage
                                                </Link>
                                            </Button>
                                        )}
                                        <Button size="sm" variant="outline" asChild>
                                            <Link to={`/app/teams/${teamInfo.team.id}`}>
                                                View
                                            </Link>
                                        </Button>
                                    </div>
                                </CardFooter>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Team statistics
                {teams.length > 0 && (
                    <div className="mt-12">
                        <h2 className="text-xl font-semibold mb-4">Team Overview</h2>
                        <div className="grid gap-6 md:grid-cols-3">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-2xl">{teams.length}</CardTitle>
                                    <CardDescription>Total Teams</CardDescription>
                                </CardHeader>
                            </Card>
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-2xl">
                                        {teams.filter(t => t.role === 'owner').length}
                                    </CardTitle>
                                    <CardDescription>Teams You Own</CardDescription>
                                </CardHeader>
                            </Card>
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-2xl">
                                        {teams.filter(t => t.role === 'admin' || t.role === 'owner').length}
                                    </CardTitle>
                                    <CardDescription>Teams You Admin</CardDescription>
                                </CardHeader>
                            </Card>
                        </div>
                    </div>
                )} */}
            </div>
        </PageLayout>
    );
};

export default TeamsPage;
